/**
 * Event Batcher Service
 * Handles event queuing, batching, and fire-and-forget sending to analytics service
 */

import { Injectable, OnDestroy, inject } from '@angular/core';
import { EMPTY, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { AnalyticsEvent } from '../interfaces';
import { AB_TEST_ANALYTICS_SERVICE } from '../tokens';
import { DebugLogger } from '../utils/debug-logger';

export interface EventBatcherConfig {
  batchSize?: number;
  flushInterval?: number;
  maxRetries?: number;
}

@Injectable({
  providedIn: 'root',
})
export class EventBatcherService implements OnDestroy {
  private eventQueue: AnalyticsEvent[] = [];
  private batchSize = 50;
  private flushInterval = 5000; // 5 seconds
  private maxRetries = 3;
  private flushTimer?: ReturnType<typeof setTimeout>;
  private pageUnloadHandler: () => void;

  private analyticsService = inject(AB_TEST_ANALYTICS_SERVICE);
  private logger = inject(DebugLogger);

  constructor() {
    // Bind the page unload handler
    this.pageUnloadHandler = this.onPageUnload.bind(this);

    // Set up browser unload event listeners to flush events on page close/refresh
    // Note: UserJourneyTrackerService also has these listeners to create page_exit events
    // before calling forceFlush(). This serves as a safety net for apps without journey tracking.
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.pageUnloadHandler);
      window.addEventListener('pagehide', this.pageUnloadHandler);
    }
  }

  /**
   * Add an event to the queue for batched sending
   */
  addEvent(event: AnalyticsEvent): void {
    this.eventQueue.push(event);

    // Start flush timer if not already running
    if (!this.flushTimer) {
      this.startFlushTimer();
    }

    // Immediate flush if batch size reached
    if (this.eventQueue.length >= this.batchSize) {
      this.flush();
    }
  }

  /**
   * Force immediate flush of all queued events
   */
  forceFlush(): void {
    this.flush();
  }

  /**
   * Configure batching behavior
   */
  configureBatching(config: EventBatcherConfig): void {
    if (config.batchSize !== undefined) {
      if (config.batchSize <= 0) {
        throw new Error('Batch size must be greater than 0');
      }
      this.batchSize = config.batchSize;
    }

    if (config.flushInterval !== undefined) {
      if (config.flushInterval <= 0) {
        throw new Error('Flush interval must be greater than 0');
      }
      this.flushInterval = config.flushInterval;
    }

    if (config.maxRetries !== undefined) {
      if (config.maxRetries < 0) {
        throw new Error('Max retries must be 0 or greater');
      }
      this.maxRetries = config.maxRetries;
    }
  }

  /**
   * Handle page unload to flush remaining events
   */
  onPageUnload(): void {
    // Flush all queued events
    // Note: UserJourneyTrackerService handles its own page_exit event creation
    // before calling forceFlush()
    if (this.eventQueue.length > 0) {
      this.flush();
    }
  }

  /**
   * Get current queue size (for testing)
   */
  getQueueSize(): number {
    return this.eventQueue.length;
  }

  /**
   * Get queued events (for testing)
   */
  getQueuedEvents(): AnalyticsEvent[] {
    return [...this.eventQueue];
  }

  /**
   * Get current batch size configuration
   */
  getBatchSize(): number {
    return this.batchSize;
  }

  /**
   * Get current flush interval configuration
   */
  getFlushInterval(): number {
    return this.flushInterval;
  }

  /**
   * Get current max retries configuration
   */
  getMaxRetries(): number {
    return this.maxRetries;
  }

  /**
   * Start the flush timer
   */
  private startFlushTimer(): void {
    this.flushTimer = setTimeout(() => {
      this.flush();
    }, this.flushInterval);
  }

  /**
   * Clear the flush timer
   */
  private clearFlushTimer(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = undefined;
    }
  }

  /**
   * Flush queued events to analytics service
   */
  private flush(): void {
    // Clear the timer
    this.clearFlushTimer();

    if (this.eventQueue.length === 0) {
      return;
    }

    // Take a snapshot of current queue and clear it
    const batch = [...this.eventQueue];
    this.eventQueue = [];

    // Split events by category
    const abTestEvents = batch.filter((event) => event.eventCategory === 'ab-test');
    const userJourneyEvents = batch.filter((event) => event.eventCategory === 'user-journey');

    // Send ab-test events
    if (abTestEvents.length > 0) {
      this.sendEventsWithRetry(abTestEvents, 'ab-test');
    }

    // Send user-journey events
    if (userJourneyEvents.length > 0) {
      this.sendEventsWithRetry(userJourneyEvents, 'user-journey');
    }
  }

  /**
   * Send events with retry logic (fire-and-forget)
   */
  private sendEventsWithRetry(events: AnalyticsEvent[], category: 'ab-test' | 'user-journey'): void {
    // Check if analytics service is available
    if (!this.analyticsService || !this.analyticsService.sendEvents) {
      return;
    }

    let observable;

    if (this.maxRetries === 0) {
      observable = this.analyticsService.sendEvents(events, category).pipe(
        catchError((error) => {
          this.logger.error(`Failed to send ${category} events:`, error);
          return EMPTY;
        })
      );
    } else {
      observable = this.analyticsService.sendEvents(events, category).pipe(
        retry({
          count: this.maxRetries,
          delay: (error, retryIndex) => {
            // Exponential backoff: 1s, 2s, 4s, ...
            const delayTime = (Math.pow(2, retryIndex) * 1000) / 2;
            return timer(delayTime);
          },
          resetOnSuccess: true,
        }),
        catchError((error) => {
          this.logger.error(`Failed to send ${category} events after retries:`, error);
          return EMPTY;
        })
      );
    }

    // Subscribe to actually send the events (fire-and-forget)
    observable.subscribe();
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy(): void {
    // Remove event listeners
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.pageUnloadHandler);
      window.removeEventListener('pagehide', this.pageUnloadHandler);
    }

    // Clear timer
    this.clearFlushTimer();

    // Flush remaining events without retry
    if (this.eventQueue.length > 0) {
      this.maxRetries = 0;
      this.flush();
    }
  }
}
